import React from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { t, fmtHours } from "../helpers/format"
import { CELL_STYLES, ABSENT_STYLE, onDark } from "../helpers/states"
import Checkbox from "./Checkbox"


const tooltip = (cell, day) => {
  const lines = []
  if (cell.state !== "empty") lines.push(t(`legend.${cell.state}`))
  if (cell.projects?.length) lines.push(cell.projects.join("\n"))
  if (cell.planned > 0 && cell.planned !== cell.hours) lines.push(t("cell.planned", { hours: fmtHours(cell.planned) }))
  if (cell.over_max) lines.push(t("legend.over_max"))
  if (cell.over_week) lines.push(t("cell.over_week"))
  if (cell.geofence) lines.push(t("legend.geofence"))
  if (cell.running) lines.push(t("legend.running"))
  if (cell.meal) lines.push(t("legend.meal"))
  if (cell.zone) lines.push(t("cell.zone", { zone: cell.zone }))
  if (cell.absence != null) lines.push(cell.absence || t("cell.absent"))
  if (day.holiday) lines.push(day.holiday)
  return lines.join("\n")
}

const Cell = ({ day, cell, mode, selected, selectable, flash, active, onToggle, onOpen }) => {
  if (!cell) return <div className="border-b border-slate-100" />

  const compact   = mode === "month"
  const hasHours  = cell.hours > 0
  const absentDay = cell.absence != null && !hasHours
  const dark      = absentDay || onDark(cell.state)
  const showPlan  = !compact && cell.planned > 0 && Math.abs(cell.planned - cell.hours) > 0.009

  return (
    <div
      className = {clsx(
        "relative p-[3px] border-b border-slate-100",
        cell.over_week ? "bg-orange-300" : day.today ? "bg-brand-50/50" : (day.weekend || day.holiday) && "bg-slate-50/70"
      )}
    >
      <div
        role      = "button"
        tabIndex  = {0}
        onClick   = {onOpen}
        onKeyDown = {e => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen() } }}
        title     = {tooltip(cell, day)}
        className = {clsx(
          "group/cell relative w-full flex cursor-pointer select-none flex-col items-center justify-center rounded-md transition-all duration-150",
          "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-500/40",
          compact ? "min-h-[50px] px-0.5" : "min-h-[64px] px-1.5",
          absentDay ? ABSENT_STYLE : CELL_STYLES[cell.state] || CELL_STYLES.empty,
          selected && "!ring-2 !ring-brand-500",
          active && "!ring-2 !ring-slate-900"
        )}
      >
        {/* top left: selection, or the lock of a closed period */}
        {cell.state === "closed" ? (
          <FontAwesomeIcon icon="lock" className="absolute top-[5px] left-[5px] text-[9px] text-white/80" />
        ) : selectable && (
          <span className={clsx("absolute top-[4px] left-[4px] transition-opacity", selected ? "opacity-100" : "opacity-0 group-hover/cell:opacity-100")}>
            <Checkbox checked={selected} onChange={onToggle} label={t("grid.select_day")} onDark={dark} />
          </span>
        )}

        {/* top right: over the daily maximum, clocked right now */}
        <span className="absolute top-[4px] right-[4px] flex items-center gap-1">
          {cell.running && (
            <span className="relative inline-flex w-[8px] h-[8px]">
              <span className="absolute inline-flex w-full h-full rounded-full bg-brand-400 opacity-75 animate-ping" />
              <span className="relative inline-flex w-[8px] h-[8px] rounded-full bg-brand-500 ring-1 ring-white" />
            </span>
          )}
          {cell.over_max && (
            <span className="inline-grid place-items-center w-[14px] h-[14px] rounded-full bg-rose-600 text-white text-[9px] font-bold ring-1 ring-white">!</span>
          )}
        </span>

        {absentDay ? (
          <span className={clsx("max-w-full truncate font-medium", compact ? "text-[9.5px]" : "text-[11px]")}>
            {cell.absence || t("cell.absent")}
          </span>
        ) : hasHours ? (
          <>
            <span className={clsx("font-semibold tabular-nums leading-none", compact ? "text-[13px]" : "text-[17px]")}>
              {fmtHours(cell.hours)}
            </span>
            {showPlan && (
              <span className={clsx("mt-1 text-[10px] tabular-nums leading-none", dark ? "text-white/75" : "opacity-70")}>
                {t("cell.planned_short", { hours: fmtHours(cell.planned) })}
              </span>
            )}
          </>
        ) : (
          <span className="text-[12px] text-slate-300">–</span>
        )}

        {/* bottom: projects of the day, allowances, absence beside hours */}
        {(hasHours || cell.colors?.length > 0) && (
          <span className={clsx("absolute left-[5px] right-[5px] flex items-center justify-between gap-1", compact ? "bottom-[5px]" : "bottom-[7px]")}>
            <span />
            {!compact && (
              <span className={clsx("flex items-center gap-1 text-[9.5px] font-semibold", dark ? "text-white/85" : "text-slate-700/70")}>
                {cell.absence != null && <FontAwesomeIcon icon="umbrella-beach" className={dark ? "text-white/85" : "text-violet-600"} />}
                {cell.geofence && <FontAwesomeIcon icon="location-dot" className={dark ? "text-amber-200" : "text-orange-700"} />}
                {cell.meal && <FontAwesomeIcon icon="utensils" />}
                {cell.zone && <span className="tabular-nums">{cell.zone}</span>}
              </span>
            )}
            {compact && cell.geofence && <FontAwesomeIcon icon="location-dot" className={clsx("text-[8px]", dark ? "text-amber-200" : "text-amber-500")} />}
          </span>
        )}

        {/* the projects of the day, as a strip of their colours along the bottom */}
        {cell.colors?.length > 0 && (
          <span className="absolute left-0 right-0 bottom-0 flex h-[4px] overflow-hidden rounded-b-md">
            {cell.colors.map((color, index) => (
              <span key={index} className="flex-1" style={{ backgroundColor: color }} />
            ))}
          </span>
        )}

        {flash && (
          <span className="absolute inset-0 grid place-items-center rounded-md bg-emerald-500 animate-[hb-fade-out_1.5s_ease-out_forwards]">
            <span className="inline-grid place-items-center w-[26px] h-[26px] rounded-full bg-white text-emerald-600 animate-[hb-pop_450ms_cubic-bezier(.2,.9,.3,1.3)]">
              <FontAwesomeIcon icon="check" className="text-[13px]" />
            </span>
          </span>
        )}
      </div>
    </div>
  )
}

export default Cell
